import { useId, type ReactNode } from 'react'
import { DESKTOP_QUERY, useMediaQuery } from '@/lib/useMediaQuery'
import { MOBILE_TABS, effectiveTab, tabId, type RecordTab } from './recordTabModel'
import { RecordTabs } from './RecordTabs'

/**
 * Relation page layout (design §2.3, ED-02, DS-14; spec-63 E3: v2). From 768 px: three
 * columns (360 · flexible · 360) from 1440 px with sticky, separately scrolling sides;
 * from 1024 px the right column drops below the middle; below 1024 px they stack. The
 * left column is the header — there is no big PageHeader. Below 768 px (ED-50): one
 * column with the header (identity and quick actions) on top and the extra tabs
 * "Koppelingen" (the right column) and "Gegevens" (the properties). The page owns the
 * width (spec-70): the layout fills it, with no cap and no centring.
 */
export function RecordLayout({
  back,
  label,
  header,
  properties,
  tabs,
  activeTab,
  onTabChange,
  panels,
  right,
  idPrefix,
}: {
  back?: ReactNode
  /** Accessible name of the tab list: "Contact", "Organisatie". */
  label: string
  /** Identity and quick actions. */
  header: ReactNode
  /** The property sections. */
  properties: ReactNode
  tabs: readonly RecordTab[]
  activeTab: string
  onTabChange: (key: string) => void
  /** One panel per key of `tabs`; only the active one renders. */
  panels: Record<string, ReactNode>
  /** Association cards. */
  right?: ReactNode
  /** Lets the page focus a tab (`focusTab`); generated when left out. */
  idPrefix?: string
}) {
  const desktop = useMediaQuery(DESKTOP_QUERY)
  const generated = useId()
  const id = idPrefix ?? generated
  // Without a right column there is no Koppelingen tab on a phone.
  const mobileTabs = right ? MOBILE_TABS : MOBILE_TABS.filter((tab) => tab.key !== 'koppelingen')
  const current = effectiveTab(activeTab, tabs, desktop, mobileTabs)
  const shown = desktop ? tabs : [...tabs, ...mobileTabs]
  const content =
    current === 'gegevens' ? (
      <section className="vonk-card flex flex-col gap-3 p-5">{properties}</section>
    ) : current === 'koppelingen' ? (
      right
    ) : (
      panels[current]
    )
  const tablist = <RecordTabs label={label} tabs={shown} active={current} onSelect={onTabChange} idPrefix={id} />
  const panel = (
    <div role="tabpanel" id={`${id}-panel`} aria-labelledby={tabId(id, current)} className="flex min-w-0 flex-col gap-4">
      {content}
    </div>
  )

  if (!desktop) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-4">
        {back}
        <div className="vonk-card flex flex-col gap-3 p-5">{header}</div>
        {tablist}
        {panel}
      </div>
    )
  }
  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      {back}
      <div className="grid items-start gap-6 min-[1024px]:grid-cols-[360px_minmax(0,1fr)] min-[1440px]:grid-cols-[360px_minmax(0,1fr)_360px]">
        <aside
          aria-label="Gegevens"
          className="flex min-w-0 flex-col gap-4 min-[1024px]:row-span-2 min-[1440px]:sticky min-[1440px]:top-0 min-[1440px]:row-span-1 min-[1440px]:max-h-[calc(100vh-6rem)] min-[1440px]:overflow-y-auto"
        >
          <section className="vonk-card flex flex-col gap-3 p-5">
            {header}
            {properties}
          </section>
        </aside>
        <div className="flex min-w-0 flex-col gap-4 min-[1024px]:col-start-2">
          {tablist}
          {panel}
        </div>
        {right ? (
          <aside
            aria-label="Koppelingen"
            className="flex min-w-0 flex-col gap-4 min-[1024px]:col-start-2 min-[1440px]:sticky min-[1440px]:top-0 min-[1440px]:col-start-3 min-[1440px]:row-start-1 min-[1440px]:max-h-[calc(100vh-6rem)] min-[1440px]:overflow-y-auto"
          >
            {right}
          </aside>
        ) : null}
      </div>
    </div>
  )
}
